import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, Users, TrendingUp, Sparkles, RefreshCcw, ChevronDown, ChevronRight,
} from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

/**
 * AdminOnboarding — archetype / quiz-answer analytics for the lab.
 *
 * Reads /api/onboarding/admin/insights. Renders:
 *  1. Summary tiles (total completions in window, top archetype, top pain)
 *  2. Archetype distribution bar chart
 *  3. Per-question option distribution
 *  4. Recent completions table with expandable per-user answers
 */
export default function AdminOnboarding() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(new Set());

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/onboarding/admin/insights", { params: { days } });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load onboarding insights");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [days]);   // eslint-disable-line react-hooks/exhaustive-deps

  const questionLabel = (qid, oid) => {
    const q = data?.questions?.find((x) => x.id === qid);
    return q?.options?.find((o) => o.id === oid)?.label || oid;
  };

  const topArchetype = data?.archetypes?.[0];
  const topChapter = data?.chapters?.[0];

  const toggleRow = (uid) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid); else next.add(uid);
      return next;
    });
  };

  const painDistribution = useMemo(() => {
    if (!data) return [];
    const d = data.distribution?.pain || {};
    const total = Object.values(d).reduce((a, b) => a + b, 0);
    return Object.entries(d)
      .sort((a, b) => b[1] - a[1])
      .map(([oid, count]) => ({
        oid,
        label: questionLabel("pain", oid),
        count,
        pct: total ? Math.round((count / total) * 100) : 0,
      }));
  }, [data]);           // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="admin-onboarding-root" className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.28em] text-amber-600 font-semibold mb-2">
            Onboarding · Quiz insights
          </div>
          <h1 className="font-display font-bold text-3xl text-slate-900 tracking-tight">
            Who&apos;s signing up — and what they came here for
          </h1>
          <p className="mt-2 text-sm text-slate-600 max-w-2xl">
            Every free signup passes through the five-question quiz. This is
            the aggregated view — archetype mix, pain signal, and each
            member&apos;s exact answers for the review queue.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">
            Window
          </label>
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
            data-testid="admin-onboarding-window"
            className="h-10 px-3 rounded-md border border-slate-300 bg-white text-sm font-semibold text-slate-700 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last 12 months</option>
          </select>
          <Button onClick={load} variant="outline" className="h-10 border-slate-300">
            <RefreshCcw size={13} className="mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      {loading || !data ? (
        <div className="flex items-center justify-center py-16 text-slate-500 text-sm">
          <Loader2 size={16} className="animate-spin mr-2" /> Loading…
        </div>
      ) : data.total_completions === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-slate-300">
          <Sparkles size={28} className="mx-auto text-slate-400 mb-3" />
          <p className="text-sm text-slate-600 font-semibold">
            No quiz completions in this window yet.
          </p>
          <p className="text-xs text-slate-500 mt-1">
            As new members sign up their archetype and answers show up here.
          </p>
        </div>
      ) : (
        <>
          {/* Summary tiles */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 inline-flex items-center gap-1.5">
                <Users size={11} /> Completions
              </div>
              <div className="mt-1 font-display font-black text-4xl text-slate-900 tabular-nums">
                {data.total_completions}
              </div>
              <div className="mt-1 text-xs text-slate-500">
                In the last {data.window_days} days
              </div>
            </div>
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-5">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-amber-700 inline-flex items-center gap-1.5">
                <Sparkles size={11} /> Top archetype
              </div>
              <div className="mt-1 font-display font-black text-xl text-amber-900 leading-tight">
                {topArchetype?.archetype || "—"}
              </div>
              <div className="mt-1 text-xs text-amber-800/80 font-medium">
                {topArchetype?.count || 0} members
              </div>
            </div>
            <div className="rounded-xl border border-blue-200 bg-blue-50 p-5">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-blue-700 inline-flex items-center gap-1.5">
                <TrendingUp size={11} /> Most-picked starting chapter
              </div>
              <div className="mt-1 font-display font-bold text-sm text-blue-900 leading-snug">
                {topChapter?.chapter || "—"}
              </div>
              <div className="mt-1 text-xs text-blue-800/80 font-medium">
                {topChapter?.count || 0} members
              </div>
            </div>
          </div>

          {/* Archetype distribution */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-4">
              Archetype distribution
            </div>
            <div className="space-y-2">
              {data.archetypes.map((a) => {
                const pct = data.total_completions
                  ? Math.round((a.count / data.total_completions) * 100)
                  : 0;
                return (
                  <div key={a.archetype} className="flex items-center gap-3">
                    <div className="w-56 text-sm font-semibold text-slate-800 truncate">
                      {a.archetype}
                    </div>
                    <div className="flex-1 h-3 rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-amber-400 to-orange-500"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <div className="w-24 text-right text-xs text-slate-500 font-mono tabular-nums">
                      {a.count} · {pct}%
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Pain point distribution — the answer that matters most for content roadmap */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-4">
              Biggest pain reported
            </div>
            <div className="space-y-2">
              {painDistribution.map((p) => (
                <div key={p.oid} className="flex items-center gap-3">
                  <div className="w-72 text-sm text-slate-800 truncate">{p.label}</div>
                  <div className="flex-1 h-3 rounded-full bg-slate-100 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-blue-500 to-blue-700"
                      style={{ width: `${p.pct}%` }}
                    />
                  </div>
                  <div className="w-20 text-right text-xs text-slate-500 font-mono tabular-nums">
                    {p.count} · {p.pct}%
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recent completions */}
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100">
              <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">
                Recent completions ({data.recent.length})
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
                  <tr>
                    <th className="text-left px-4 py-2 font-semibold w-8"></th>
                    <th className="text-left px-4 py-2 font-semibold">Member</th>
                    <th className="text-left px-4 py-2 font-semibold">Tier</th>
                    <th className="text-left px-4 py-2 font-semibold">Archetype</th>
                    <th className="text-left px-4 py-2 font-semibold">Signed up</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.recent.map((u) => {
                    const open = expanded.has(u.user_id);
                    return (
                      <>
                        <tr
                          key={u.user_id}
                          data-testid={`admin-onboarding-row-${u.user_id}`}
                          className="hover:bg-slate-50 cursor-pointer"
                          onClick={() => toggleRow(u.user_id)}
                        >
                          <td className="px-4 py-3 text-slate-400">
                            {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                          </td>
                          <td className="px-4 py-3">
                            <div className="font-semibold text-slate-900">{u.name || "—"}</div>
                            <div className="text-[11px] text-slate-500 font-mono">{u.email}</div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded border ${
                              u.tier === "premium" ? "bg-amber-50 text-amber-700 border-amber-200"
                              : u.tier === "basic" ? "bg-blue-50 text-blue-700 border-blue-200"
                              : "bg-slate-100 text-slate-700 border-slate-200"
                            }`}>
                              {u.tier}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-slate-800 font-semibold">{u.archetype}</td>
                          <td className="px-4 py-3 text-xs text-slate-500">
                            {u.completed_at ? new Date(u.completed_at).toLocaleDateString() : "—"}
                          </td>
                        </tr>
                        {open && (
                          <tr key={`${u.user_id}-detail`} className="bg-slate-50/60">
                            <td colSpan={5} className="px-4 py-4">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 text-xs">
                                {Object.entries(u.answers).map(([qid, oid]) => (
                                  <div key={qid} className="flex flex-col">
                                    <span className="uppercase tracking-widest font-bold text-slate-500 text-[10px]">
                                      {qid}
                                    </span>
                                    <span className="text-slate-800">{questionLabel(qid, oid)}</span>
                                  </div>
                                ))}
                                <div className="md:col-span-2 pt-2 border-t border-slate-200">
                                  <span className="uppercase tracking-widest font-bold text-slate-500 text-[10px]">
                                    Recommended chapter
                                  </span>
                                  <div className="text-slate-800 font-semibold">{u.starting_chapter}</div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
